import { DealViews, defaultDealsQueries } from "constants/deal";
import { DealAction } from "store/actions";

import { IAction, initialState } from "../..";
import { RoleName } from "../../../constants";
import { dealModule } from "../../store-context";
import {
  CurrencyType,
  DealChunks,
  DealsQueries,
  IAssetClass,
  ICapitalSeeker,
  IDashboardShare,
  IDeal,
  IDealModule,
  IDealRoundsWithInvestors,
  IFile,
  INewsMeta,
  IReachOutManage,
  IRole,
  IStatus,
  ITransformedChartConfig,
  IUploadingProgress,
  MeasureParameter,
  ReportFilters
} from "../../types";
import { reducer } from "../index";

reducer.createHandler(DealAction.GetDealByIdSuccess, (state, { payload }: IAction<IDeal>) => ({
  ...state,
  dealModule: {
    ...state.dealModule,
    loading: false,
    deal: payload
  },
  // Switch report currency to deal currency when deal fetched
  reports: {
    ...state.reports,
    queries: {
      ...state.reports.queries,
      currency: payload.currency?.data?.find((curr) => curr.is_default)?.value as CurrencyType
    }
  }
}));

reducer.createHandler(DealAction.UpdateDealSuccess, (state, { payload }: IAction<IDeal>) => {
  return {
    ...state,
    dealModule: {
      ...state.dealModule,
      deal: {
        ...state.dealModule.deal,
        ...payload
      }
    }
  };
});

reducer.createHandler(DealAction.DealInvalidate, (state) => ({
  ...state,
  dealModule: {
    ...state.dealModule,
    deal: dealModule.deal,
    folders: initialState.dealModule.folders,
    folderMeta: initialState.dealModule.folderMeta,
    files: initialState.dealModule.files,
    forms: initialState.dealModule.forms,
    notes: initialState.dealModule.notes,
    emails: initialState.dealModule.emails,
    roundsWithInvestors: initialState.dealModule.roundsWithInvestors
  },
  documents: initialState.documents
}));

reducer.createHandler(DealAction.GetDealStatusesSuccess, (state, { payload }: IAction<IStatus[]>) => ({
  ...state,
  dealModule: {
    ...state.dealModule,
    statuses: [...state.dealModule.statuses, ...payload]
  }
}));

reducer.createHandler(
  DealAction.UpdateDealStatusSuccess,
  (state, { payload }: IAction<{ dealId: number } & IStatus>) => {
    const { dealId, ...status } = payload;
    if (dealId !== state.dealModule.deal.id) return state;

    return {
      ...state,
      dealModule: {
        ...state.dealModule,
        deal: {
          ...state.dealModule.deal,
          status
        }
      }
    };
  }
);

reducer.createHandler(DealAction.UploadLoadingProgress, (state, { payload }: IAction<IUploadingProgress>) => ({
  ...state,
  dealModule: {
    ...state.dealModule,
    uploadingProgress: state.dealModule.uploadingProgress.some(
      (loading: IUploadingProgress) => loading.uuid === payload.uuid
    )
      ? state.dealModule.uploadingProgress.map((loading: IUploadingProgress) => {
          if (loading.uuid === payload.uuid) {
            return payload;
          }
          return loading;
        })
      : [...state.dealModule.uploadingProgress, payload]
  }
}));

reducer.createHandler(DealAction.UploadLoadingSuccess, (state, { payload }: IAction<string>) => {
  return {
    ...state,
    dealModule: {
      ...state.dealModule,
      uploadingProgress: state.dealModule.uploadingProgress.filter(
        (loading: IUploadingProgress) => loading.uuid !== payload
      )
    }
  };
});

reducer.createHandler(DealAction.UploadLoadingFailure, (state) => {
  return {
    ...state,
    dealModule: {
      ...state.dealModule,
      uploadingProgress: []
    }
  };
});

reducer.createHandler(DealAction.GetAssetClassesSuccess, (state, { payload }: IAction<IAssetClass[]>) => ({
  ...state,
  dealModule: {
    ...state.dealModule,
    assetClasses: payload
  }
}));

reducer.createHandler(DealAction.UpdateCapitalSeekerSuccess, (state, { payload }: IAction<ICapitalSeeker[]>) => ({
  ...state,
  dealModule: {
    ...state.dealModule,
    deal: {
      ...state.dealModule.deal,
      deal_capital_seeker: payload
    }
  }
}));

reducer.createHandler(DealAction.GetNewsMetaSuccess, (state, { payload: newsMeta }: IAction<INewsMeta>) => ({
  ...state,
  dealModule: {
    ...state.dealModule,
    newsMeta
  }
}));

reducer.createHandler(DealAction.InvalidateNewslMeta, (state) => ({
  ...state,
  dealModule: {
    ...state.dealModule,
    newsMeta: {}
  }
}));

reducer.createHandler(DealAction.GetMeasureParametersSuccess, (state, { payload }: IAction<MeasureParameter[]>) => ({
  ...state,
  dealModule: {
    ...state.dealModule,
    measureParameters: payload
  }
}));

reducer.createHandler(
  DealAction.UpdateQueries,
  (
    state,
    { payload: { queries, isFullReplace } }: IAction<{ queries: Partial<DealsQueries>; isFullReplace?: boolean }>
  ) => {
    const canSeePipeline = [RoleName.Admin, RoleName.Analyst] as IRole["name"][];

    const userRoleName = state.userModule.user.role.name;

    // Set default deal view grid for external users when reset queries to default
    const dealQueriesByRole: DealsQueries = {
      ...defaultDealsQueries,
      ...(!canSeePipeline.includes(userRoleName) && { view: DealViews.GRID })
    };

    return {
      ...state,
      dealModule: {
        ...state.dealModule,
        queries: isFullReplace
          ? {
              ...dealQueriesByRole,
              ...queries,
              //if queries dont have chartInvestorView then use old chartInvestorView from state (user settings)
              //fix for old bookmarks that dont have chartInvestorView
              chartInvestorView:
                queries.chartInvestorView && Object.keys(queries.chartInvestorView).length
                  ? queries.chartInvestorView
                  : state.dealModule.queries.chartInvestorView
            }
          : { ...state.dealModule.queries, ...queries }
      },
      reports: isFullReplace
        ? {
            ...state.reports,
            ignoreDefaultView: true
          }
        : state.reports
    };
  }
);

reducer.createHandler<{
  cubicKey: string;
  filter: string;
  title: string;
  indexName?: { index: string; name: string };
}>(DealAction.SetDealsQueryCubic, (state, { payload: { cubicKey, filter, title, indexName } }) => ({
  ...state,
  dealModule: {
    ...state.dealModule,
    queries: {
      ...state.dealModule.queries,
      cubicInvestorView: {
        ...state.dealModule.queries.cubicInvestorView,
        [cubicKey]: { filter, title, indexName }
      }
    }
  }
}));

reducer.createHandler(DealAction.SetLogoToSave, (state, { payload }: IAction<IFile | null>) => ({
  ...state,
  dealModule: {
    ...state.dealModule,
    logoToSave: payload
  }
}));

reducer.createHandler(DealAction.SaveLogoSuccess, (state, { payload }: IAction<IFile>) => ({
  ...state,
  dealModule: {
    ...state.dealModule,
    deal: {
      ...state.dealModule.deal,
      logo: payload
    }
  }
}));

reducer.createHandler(DealAction.DeleteLogoSuccess, (state) => ({
  ...state,
  dealModule: {
    ...state.dealModule,
    deal: {
      ...state.dealModule.deal,
      logo: null
    }
  }
}));

reducer.createHandler(DealAction.SetHierarchyPanelOpen, (state, { payload }) => ({
  ...state,
  dealModule: {
    ...state.dealModule,
    hierarchyPanelOpen: payload
  }
}));

reducer.createHandler<IDealModule["characteristicsManage"]>(
  DealAction.SetCharacteristicsManageData,
  (state, { payload }) => ({
    ...state,
    dealModule: {
      ...state.dealModule,
      characteristicsManage: payload
    }
  })
);

reducer.createHandler(DealAction.SetReachOutManage, (state, { payload }: IAction<IReachOutManage | null>) => ({
  ...state,
  dealModule: {
    ...state.dealModule,
    reachOutManage: payload
  }
}));

reducer.createHandler(DealAction.GetDealChunksSuccess, (state, { payload }: IAction<DealChunks>) => {
  const { documents, ...rest } = payload;
  return {
    ...state,
    dealModule: { ...state.dealModule, ...rest },
    documents
  };
});

reducer.createHandler(DealAction.CreateCrunchbaseSuccess, (state, { payload }: IAction<number>) => ({
  ...state,
  dealModule: {
    ...state.dealModule,
    deal: {
      ...state.dealModule.deal,
      crunchbase_data_id: payload
    }
  }
}));

reducer.createHandler(DealAction.GetDashboardSharesSuccess, (state, { payload }: IAction<IDashboardShare[]>) => ({
  ...state,
  dealModule: {
    ...state.dealModule,
    shares: payload
  }
}));

reducer.createHandler(DealAction.GetDealFiltersSuccess, (state, { payload }: IAction<ReportFilters>) => ({
  ...state,
  dealModule: {
    ...state.dealModule,
    dealFilters: payload
  }
}));

reducer.createHandler(DealAction.UpdateDealCryptoCoinIcon, (state, { payload }: IAction<string>) => ({
  ...state,
  dealModule: {
    ...state.dealModule,
    deal: {
      ...state.dealModule.deal,
      crypto_coin_icon: payload
    }
  }
}));

reducer.createHandler(DealAction.UpdateDealNullifyCommitment, (state, { payload }: IAction<boolean>) => ({
  ...state,
  dealModule: {
    ...state.dealModule,
    deal: {
      ...state.dealModule.deal,
      nullify_unfunded_commitment: payload
    }
  }
}));

reducer.createHandler<{ key: string; measure?: string; groupBy?: string }>(
  DealAction.SetInvestorsViewChart,
  (state, { payload: { key, ...rest } }) => ({
    ...state,
    dealModule: {
      ...state.dealModule,
      queries: {
        ...state.dealModule.queries,
        chartInvestorView: {
          ...state.dealModule.queries.chartInvestorView,
          [key]: {
            ...state.dealModule.queries.chartInvestorView[key],
            ...rest
          }
        }
      }
    }
  })
);

reducer.createHandler(
  DealAction.SetInvestmentDetailsChart,
  (state, { payload: { chartName, ...rest } }: IAction<ITransformedChartConfig>) => ({
    ...state,
    dealModule: {
      ...state.dealModule,
      deal: {
        ...state.dealModule.deal,
        chart_settings: {
          ...state.dealModule.deal.chart_settings,
          [chartName]: {
            ...state.dealModule.deal.chart_settings[chartName],
            value_by: rest.valueBy,
            group_by: rest.groupBy
          }
        }
      }
    }
  })
);

reducer.createHandler(
  DealAction.GetDealRoundsWithInvestorsSuccess,
  (state, { payload }: IAction<IDealRoundsWithInvestors[]>) => ({
    ...state,
    dealModule: {
      ...state.dealModule,
      roundsWithInvestors: payload
    }
  })
);
